import { useState } from 'react';
import type { RunEvent } from './api';

// Kind tone + label live together so the legend in the trace stays in sync
// with the rendering of individual blocks. Order matters for the legend.
export const EVENT_KIND: Record<
  string,
  { label: string; tone: string; bar: string; icon: string }
> = {
  llm_msg: {
    label: 'LLM',
    tone: 'border-line-soft bg-surface-white',
    bar: 'bg-ink-tertiary',
    icon: '◐',
  },
  tool_call: {
    label: 'Tool call',
    tone: 'border-bit2me/30 bg-bit2me/5',
    bar: 'bg-bit2me',
    icon: '▶',
  },
  tool_result: {
    label: 'Tool result',
    tone: 'border-line-soft bg-surface-soft/40',
    bar: 'bg-ink-secondary',
    icon: '◀',
  },
  sign: {
    label: 'Sign',
    tone: 'border-purple-200 bg-purple-50',
    bar: 'bg-purple-500',
    icon: '✎',
  },
  error: {
    label: 'Error',
    tone: 'border-danger/30 bg-danger/5',
    bar: 'bg-danger',
    icon: '⚠',
  },
  final: {
    label: 'Final',
    tone: 'border-ok/30 bg-ok/5',
    bar: 'bg-ok',
    icon: '✓',
  },
};

const EVM_EXPLORERS: Record<string, string> = {
  ethereum: 'https://etherscan.io/tx/',
  polygon: 'https://polygonscan.com/tx/',
  bsc: 'https://bscscan.com/tx/',
  arbitrum: 'https://arbiscan.io/tx/',
  optimism: 'https://optimistic.etherscan.io/tx/',
  base: 'https://basescan.org/tx/',
  sepolia: 'https://sepolia.etherscan.io/tx/',
};

export function RunEventBlock({ event }: { event: RunEvent }) {
  const meta = EVENT_KIND[event.kind] ?? EVENT_KIND.llm_msg;
  return (
    <div className={`overflow-hidden rounded-lg border ${meta.tone}`}>
      <div className="flex">
        <div className={`w-1 shrink-0 ${meta.bar}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3 border-b border-line-soft/60 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
                #{String(event.ord).padStart(3, '0')} {meta.icon} {meta.label}
              </span>
            </div>
            <span className="font-mono text-[10px] text-ink-tertiary">
              {new Date(event.created_at).toLocaleTimeString()}
            </span>
          </div>
          <EventBody event={event} />
        </div>
      </div>
    </div>
  );
}

function EventBody({ event }: { event: RunEvent }) {
  const { kind, payload } = event;
  if (kind === 'llm_msg') {
    return <LLMMessage payload={payload} />;
  }
  if (kind === 'tool_call') {
    return <ToolCall payload={payload} />;
  }
  if (kind === 'tool_result') {
    return <ToolResult payload={payload} />;
  }
  if (kind === 'final') {
    return (
      <div className="px-3 py-3">
        <p className="whitespace-pre-wrap text-sm text-ink-primary">
          {String(payload.summary ?? '')}
        </p>
        {payload.outcome ? (
          <p className="mt-2 font-mono text-[11px] text-ink-tertiary">
            outcome: {String(payload.outcome)}
          </p>
        ) : null}
      </div>
    );
  }
  if (kind === 'error') {
    return (
      <div className="px-3 py-3">
        <p className="whitespace-pre-wrap text-sm text-danger">
          {String(payload.message ?? payload.error ?? 'Unknown error')}
        </p>
      </div>
    );
  }
  return (
    <pre className="max-h-40 overflow-auto whitespace-pre-wrap px-3 py-3 text-xs text-ink-secondary">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

function LLMMessage({ payload }: { payload: Record<string, unknown> }) {
  const text = String(payload.text ?? '');
  if (!text) {
    return (
      <p className="px-3 py-3 text-xs italic text-ink-tertiary">
        (no text — the model returned tool calls only)
      </p>
    );
  }
  return (
    <div className="px-3 py-3">
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-primary">{text}</p>
    </div>
  );
}

function ToolCall({ payload }: { payload: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(true);
  const name = String(payload.name ?? 'unknown');
  const input = payload.input;
  return (
    <div className="space-y-2 px-3 py-3">
      <div className="flex items-center gap-2">
        <code className="font-mono text-sm font-semibold text-bit2me">{name}</code>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="font-mono text-[10px] text-ink-tertiary hover:text-ink-primary"
        >
          {expanded ? 'hide' : 'show'} args
        </button>
        <CopyButton text={pretty(input)} />
      </div>
      {expanded && (
        <pre className="max-h-64 overflow-auto rounded-md border border-line-soft bg-surface-white px-3 py-2 text-xs text-ink-secondary">
          {pretty(input)}
        </pre>
      )}
    </div>
  );
}

function ToolResult({ payload }: { payload: Record<string, unknown> }) {
  // Payment tool results carry a tx_hash + chain + recipient — render a
  // dedicated card so the operator doesn't have to dig through JSON to see
  // what the agent actually did.
  if (payload.tx_hash && payload.chain) {
    return <PaymentResult payload={payload} />;
  }
  const output = payload.output;
  const raw = typeof output === 'string' ? output : pretty(output ?? payload);
  return <ScrollableJSON value={raw} />;
}

function PaymentResult({ payload }: { payload: Record<string, unknown> }) {
  const chain = String(payload.chain ?? '');
  const txHash = String(payload.tx_hash ?? '');
  const recipient = String(payload.recipient ?? '');
  const amount = String(payload.amount ?? '');
  const asset = String(payload.asset ?? 'native');
  const status = String(payload.status ?? 'broadcast');
  const explorer = EVM_EXPLORERS[chain];

  const statusTone =
    status === 'confirmed'
      ? 'text-ok'
      : status === 'reverted' || status === 'failed'
      ? 'text-danger'
      : status === 'timeout'
      ? 'text-amber-600'
      : 'text-bit2me';

  return (
    <div className="space-y-2 px-3 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-base font-semibold text-ink-primary">
          {amount} {asset.toUpperCase()}
        </p>
        <p className="font-mono text-xs text-ink-tertiary">→ {shortenAddress(recipient)}</p>
        <span className={`font-mono text-[10px] uppercase tracking-eyebrow ${statusTone}`}>
          {status}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
        <span className="rounded border border-line-soft bg-surface-white px-1.5 py-0.5 text-ink-secondary">
          {chain}
        </span>
        <span className="text-ink-tertiary">{shortenAddress(txHash, 10)}</span>
        <CopyButton text={txHash} />
        {explorer && (
          <a
            href={`${explorer}${txHash}`}
            target="_blank"
            rel="noreferrer"
            className="text-bit2me hover:underline"
          >
            View on explorer ↗
          </a>
        )}
      </div>
    </div>
  );
}

function ScrollableJSON({ value }: { value: string }) {
  return (
    <div className="relative">
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap px-3 py-3 text-xs text-ink-secondary">
        {value}
      </pre>
      <div className="absolute right-2 top-2">
        <CopyButton text={value} />
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore silently.
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded border border-line-soft bg-surface-white px-1.5 py-0.5 font-mono text-[10px] text-ink-secondary hover:border-ink-secondary hover:text-ink-primary"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

function pretty(v: unknown): string {
  try {
    return JSON.stringify(v ?? {}, null, 2);
  } catch {
    return String(v);
  }
}

function shortenAddress(a: string, head = 6): string {
  if (a.length <= head + 4 + 1) return a;
  return `${a.slice(0, head)}…${a.slice(-4)}`;
}
